
import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Download, Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

const Resume = () => {
  // URLs for the resume versions - updated the ATS resume URL
  // Replace these with your actual file URLs
  const interactiveResumeUrl = "https://claude.ai/public/artifacts/e7329cf5-7857-475f-afda-9efc095a604c";
  const atsResumeUrl = "https://docs.google.com/document/d/1W29a_P_ANL3mio4HBA6JowbaqX3iV4O5_sWWD7y0pgA/edit?usp=sharing";
  
  useEffect(() => {
    // Update document title
    document.title = "Payton Hunter | Psychology Graduate | Resume";
    
    // Track page visit (simple analytics)
    console.log('Page visit: resume');
    
    // Log when the page is loaded
    const trackPageView = () => {
      // This could be expanded to include Google Analytics or other tracking services
      console.log('Resume page fully loaded');
    };
    
    window.addEventListener('load', trackPageView);
    
    return () => {
      window.removeEventListener('load', trackPageView);
    };
  }, []);
  
  // Function to track resume views and downloads
  const trackResumeInteraction = (type: string, version: string) => {
    console.log(`Resume ${type}: ${version} resume`);
    
    // Show toast notification with double quotes to prevent syntax errors
    toast(`${version} resume ${type === 'download' ? 'downloaded' : 'viewed'}`, {
      description: `You've successfully ${type === 'download' ? 'downloaded' : 'viewed'} the ${version} resume.`,
    });
  };

  // Function to handle resume download or view
  const handleResumeAction = (url: string, isDownload: boolean, version: string) => {
    // Open in new tab to ensure it works when shared externally
    window.open(url, '_blank', 'noopener,noreferrer');
    trackResumeInteraction(isDownload ? 'download' : 'view', version);
  };

  return (
    <div className="min-h-screen bg-white font-sans flex flex-col">
      <Helmet>
        <title>Payton Hunter | Resume | Psychology Graduate</title>
        <meta name="description" content="Access Payton Hunter's professional resume—Psychology graduate with forensic anthropology focus, skilled in client experience, communication, and AI practices" />
        <meta name="keywords" content="Payton Hunter, resume, CV, psychology, client relations, communication, systems improvement, AI literacy" />
        <meta property="og:title" content="Payton Hunter | Resume | Psychology Graduate" />
        <meta property="og:description" content="Access Payton Hunter's professional resume—Psychology graduate with client experience expertise" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://peytonhuntersresume.vercel.app/resume" />
        <meta property="og:image" content="https://peytonhuntersresume.vercel.app/lovable-uploads/202d7088-df35-45e9-9b34-5cae17101b80.png" />
        <meta name="robots" content="index, follow" />
        
        {/* Resume structured data */}
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org/",
              "@type": "DigitalDocument",
              "name": "Payton Hunter's Professional Resume",
              "description": "Professional resume of Payton Hunter - Psychology Graduate with client relations expertise",
              "url": "https://peytonhuntersresume.vercel.app/resume",
              "author": {
                "@type": "Person",
                "name": "Payton Hunter"
              },
              "about": {
                "@type": "Person",
                "name": "Payton Hunter",
                "jobTitle": "Psychology Graduate",
                "alumniOf": "Francis Marion University"
              },
              "encodingFormat": "text/html",
              "isPartOf": {
                "@type": "WebSite",
                "url": "https://peytonhuntersresume.vercel.app",
                "name": "Payton Hunter's Professional Portfolio"
              }
            }
          `}
        </script>
      </Helmet>
      
      <Navbar />
      
      <main className="flex-grow pt-24 pb-16 px-4">
        <div className="container max-w-5xl mx-auto">
          <div className="mb-10 text-center">
            <h1 className="text-4xl md:text-5xl font-serif mb-4">Resume</h1>
            <p className="text-lg text-sage-700 max-w-2xl mx-auto">
              View my professional experience and qualifications. Download the ATS-friendly version for job applications.
            </p>
          </div>
          
          <div className="mb-8 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button 
              className="bg-sage-700 hover:bg-sage-800 text-white flex gap-2"
              onClick={() => handleResumeAction(atsResumeUrl, true, 'ATS-friendly')}
            >
              <Download size={18} />
              Download ATS-Friendly Resume
            </Button>
            
            <Button 
              variant="outline"
              className="border-sage-600 text-sage-700 hover:bg-sage-100 flex gap-2"
              onClick={() => handleResumeAction(interactiveResumeUrl, false, 'Interactive')}
            >
              <Eye size={18} />
              View Full Interactive Resume
            </Button>
          </div>
          
          <div className="bg-white shadow-lg rounded-lg overflow-hidden border border-sage-200">
            <div className="p-4 bg-sage-50 border-b border-sage-200 flex justify-between items-center">
              <h2 className="text-xl font-serif text-sage-800">Interactive Resume Preview</h2>
              <Button 
                variant="ghost"
                size="sm"
                className="text-sage-700 hover:text-sage-900 hover:bg-sage-100"
                onClick={() => handleResumeAction(interactiveResumeUrl, false, 'Interactive')}
              >
                Open Full Version
              </Button>
            </div>
            
            <div className="h-[700px] md:h-[800px] w-full relative">
              <iframe
                src={interactiveResumeUrl}
                title="Payton Hunter's Interactive Resume"
                className="w-full h-full border-0"
                onLoad={() => console.log('Resume iframe loaded')}
                aria-label="Interactive resume preview"
              ></iframe>
              
              {/* Overlay to prevent interaction issues and provide a clear CTA */}
              <div className="absolute inset-0 bg-white/5 flex items-center justify-center">
                <Button 
                  size="lg"
                  className="bg-sage-700/90 hover:bg-sage-800 text-white shadow-xl backdrop-blur-sm"
                  onClick={() => handleResumeAction(interactiveResumeUrl, false, 'Interactive')}
                >
                  <Eye className="mr-2" size={20} />
                  View Full Resume
                </Button>
              </div>
            </div>
          </div>
          
          <div className="mt-12 bg-sage-50 rounded-lg p-6 shadow-inner">
            <h2 className="text-2xl font-serif mb-4 text-center text-sage-800">Why Two Resume Versions?</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white p-5 rounded-lg shadow-sm border border-sage-200">
                <h3 className="text-xl font-serif mb-2 text-sage-700">Interactive Resume</h3>
                <p className="text-sage-600">
                  This version showcases my experience, skills, and accomplishments in a visually appealing format. 
                  Perfect for getting a comprehensive view of my professional background.
                </p>
              </div>
              <div className="bg-white p-5 rounded-lg shadow-sm border border-sage-200">
                <h3 className="text-xl font-serif mb-2 text-sage-700">ATS-Friendly Resume</h3>
                <p className="text-sage-600">
                  Optimized for Applicant Tracking Systems used by employers. This version ensures that my qualifications 
                  are properly parsed by automated systems, maximizing the chances of my application being seen.
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default Resume;
